import { useState } from 'react'
import { Link } from 'react-router-dom'
import { ErrorBanner, useAsync } from '../../components/Async'
import { PageTitle } from '../../components/PageTitle'
import {
  ApiError,
  violationMessages,
  type PolicyReference,
  type ViolationMessage,
  type ViolationMessageDraft,
  type ViolationMessageLimits
} from '../../lib/api'
import { PRODUCT_NAME } from '../../lib/product'
import { BLANK_DRAFT, VISIBLE_WITHOUT_EXPANDING, copyOf, draftOf, measureNotice, previewNotice } from './violationMessages'

/** What the form is doing: writing a new message, or changing one the organization owns. */
type Editing = { kind: 'new'; draft: ViolationMessageDraft } | { kind: 'edit'; message: ViolationMessage; draft: ViolationMessageDraft }

/**
 * The organization's violation messages: what a policy tells the person at the keyboard when it is
 * violated (ADR-S-053).
 *
 * The point is the handbook. An organization with an acceptable use policy wants the notification to
 * name the practice that was broken, in the handbook's own words, so a message carries a reference
 * that publication fills in. The built-in messages cannot be changed, because they are shared by every
 * organization; copying one is how it becomes yours.
 */
export function ViolationMessagesPage() {
  const [nonce, setNonce] = useState(0)
  const list = useAsync(() => violationMessages.list(), [nonce])
  const [editing, setEditing] = useState<Editing | null>(null)
  const [failure, setFailure] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [republish, setRepublish] = useState<PolicyReference[]>([])
  const [retiring, setRetiring] = useState<{ message: ViolationMessage; usedBy: PolicyReference[] } | null>(null)
  const [busy, setBusy] = useState(false)

  const items = list.value?.items ?? []
  const builtIn = items.filter((item) => item.builtIn)
  const own = items.filter((item) => !item.builtIn)

  const run = async (work: () => Promise<string>) => {
    setBusy(true)
    setFailure(null)
    setNotice(null)
    try {
      setNotice(await work())
      setNonce((value) => value + 1)
    } catch (error) {
      setFailure(error instanceof ApiError ? error.message : 'That could not be saved.')
    } finally {
      setBusy(false)
    }
  }

  const save = (current: Editing) =>
    run(async () => {
      if (current.kind === 'new') {
        await violationMessages.create(current.draft)
        setEditing(null)
        setRepublish([])
        return `'${current.draft.name.trim()}' is saved. Choose it in a policy's "When a rule matches" to use it.`
      }
      const saved = await violationMessages.update(current.message.violationMessageId, current.draft, current.message.version)
      setEditing(null)
      setRepublish(saved.republish)
      return saved.republish.length === 0
        ? `'${current.draft.name.trim()}' is saved. No policy uses it yet.`
        : `'${current.draft.name.trim()}' is saved. Devices keep showing the old words until the policies below are published again.`
    })

  const askRetire = (message: ViolationMessage) =>
    run(async () => {
      const detail = await violationMessages.get(message.violationMessageId)
      if (detail.usedBy.length > 0) {
        setRetiring({ message, usedBy: detail.usedBy })
        return `${detail.usedBy.length === 1 ? 'A policy uses' : `${detail.usedBy.length} policies use`} this message.`
      }
      await violationMessages.retire(message.violationMessageId)
      return `'${message.name}' is retired.`
    })

  const forceRetire = (message: ViolationMessage) =>
    run(async () => {
      await violationMessages.retire(message.violationMessageId, true)
      setRetiring(null)
      return `'${message.name}' is retired. The policies that used it are refused at their next publish until they use another.`
    })

  return (
    <>
      <div className="page-header">
        <div>
          <PageTitle>Violation messages</PageTitle>
          <p>
            What {PRODUCT_NAME} tells the person at the keyboard when a policy stops or flags a prompt. Write
            them in your handbook's words, citing the practice that was broken, and choose one in each policy.
          </p>
        </div>
        <div className="toolbar">
          <button
            type="button"
            className="primary"
            disabled={busy}
            onClick={() => setEditing({ kind: 'new', draft: { ...BLANK_DRAFT } })}
            data-testid="new-violation-message"
          >
            New message
          </button>
        </div>
      </div>

      <ErrorBanner message={failure ?? list.error} />
      {notice && (
        <div className="banner success" role="status" data-testid="violation-message-notice">
          {notice}
        </div>
      )}

      {republish.length > 0 && (
        <div className="banner warning" data-testid="violation-message-republish">
          Publish these again to send the new words:{' '}
          {republish.map((policy, index) => (
            <span key={policy.policyId}>
              {index > 0 && ', '}
              <Link to={`/policies/${policy.policyId}`}>{policy.name}</Link>
            </span>
          ))}
          .
        </div>
      )}

      {retiring && (
        <div className="banner warning" role="alert" data-testid="violation-message-in-use">
          <p>
            '{retiring.message.name}' is used by{' '}
            {retiring.usedBy.map((policy, index) => (
              <span key={policy.policyId}>
                {index > 0 && ', '}
                <Link to={`/policies/${policy.policyId}`}>{policy.name}</Link>
              </span>
            ))}
            . Publishing {retiring.usedBy.length === 1 ? 'it' : 'them'} is refused until each uses another message.
          </p>
          <div className="toolbar">
            <button type="button" disabled={busy} onClick={() => void forceRetire(retiring.message)} data-testid="violation-message-retire-anyway">
              Retire it anyway
            </button>
            <button type="button" onClick={() => setRetiring(null)}>
              Keep it
            </button>
          </div>
        </div>
      )}

      {editing && list.value && (
        <MessageForm
          editing={editing}
          limits={list.value.limits}
          busy={busy}
          onChange={(draft) => setEditing({ ...editing, draft })}
          onSave={() => void save(editing)}
          onCancel={() => setEditing(null)}
        />
      )}

      <section className="card" style={{ marginTop: '1rem' }}>
        <div className="card-body">
          <h3>Your organization's</h3>
          {list.value && own.length === 0 ? (
            <p className="hint" data-testid="no-own-violation-messages">
              None yet. Write one with New message, or copy a built-in one below and change its words.
            </p>
          ) : (
            <ul className="binding-list" data-testid="own-violation-messages">
              {own.map((message) => (
                <li key={message.violationMessageId} data-testid={`violation-message-${message.violationMessageId}`}>
                  <MessageRow message={message} />
                  <div className="toolbar">
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => setEditing({ kind: 'edit', message, draft: draftOf(message) })}
                      data-testid={`edit-violation-message-${message.violationMessageId}`}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => void askRetire(message)}
                      data-testid={`retire-violation-message-${message.violationMessageId}`}
                    >
                      Retire
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>

      <section className="card" style={{ marginTop: '1rem' }}>
        <div className="card-body">
          <h3>Built in</h3>
          <p className="hint">Every organization has these. They cannot be changed; copy one to make it yours.</p>
          <ul className="binding-list" data-testid="built-in-violation-messages">
            {builtIn.map((message) => (
              <li key={message.violationMessageId}>
                <MessageRow message={message} />
                <div className="toolbar">
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => setEditing({ kind: 'new', draft: copyOf(message, items.map((item) => item.name)) })}
                    data-testid={`copy-violation-message-${message.violationMessageId}`}
                  >
                    Copy
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </section>
    </>
  )
}

/** One message, shown the way the notification would show it. */
function MessageRow({ message }: { message: ViolationMessage }) {
  return (
    <span className="binding-row">
      <span className="binding-name">
        {message.name}
        {message.reference && <span className="pill"> {message.reference}</span>}
      </span>
      <span className="hint">
        <strong>{previewNotice(message.title, message.reference ?? '')}</strong> ·{' '}
        {previewNotice(message.message, message.reference ?? '')}
      </span>
    </span>
  )
}

interface FormProps {
  editing: Editing
  limits: ViolationMessageLimits
  busy: boolean
  onChange: (draft: ViolationMessageDraft) => void
  onSave: () => void
  onCancel: () => void
}

function MessageForm({ editing, limits, busy, onChange, onSave, onCancel }: FormProps) {
  const { draft } = editing
  const measure = measureNotice(draft, limits)
  const set = (changes: Partial<ViolationMessageDraft>) => onChange({ ...draft, ...changes })

  return (
    <section className="card" style={{ marginTop: '1rem' }} data-testid="violation-message-form">
      <form
        className="card-body"
        onSubmit={(event) => {
          event.preventDefault()
          onSave()
        }}
      >
        <h3>{editing.kind === 'new' ? 'A new message' : `Changing '${editing.message.name}'`}</h3>

        <div className="field">
          <label htmlFor="violation-name">Name</label>
          <input
            id="violation-name"
            value={draft.name}
            maxLength={limits.name}
            onChange={(event) => set({ name: event.target.value })}
            data-testid="violation-name"
          />
          <div className="hint">Only operators see this, when choosing a message for a policy.</div>
        </div>

        <div className="field">
          <label htmlFor="violation-reference">Handbook reference</label>
          <input
            id="violation-reference"
            value={draft.reference}
            maxLength={limits.reference}
            placeholder="Acceptable Use Policy 4.2"
            onChange={(event) => set({ reference: event.target.value })}
            data-testid="violation-reference"
          />
          <div className="hint">
            The practice that was broken, as your handbook names it. It goes wherever the title or the message says{' '}
            {'{reference}'}.
          </div>
        </div>

        <div className="field">
          <label htmlFor="violation-title">Title</label>
          <input
            id="violation-title"
            value={draft.title}
            onChange={(event) => set({ title: event.target.value })}
            data-testid="violation-title"
          />
          <div className={measure.titleOver ? 'hint warn' : 'hint'} data-testid="violation-title-count">
            {measure.titleLength} of {limits.title} characters, with the reference in. The notification cuts a longer
            title, so it cannot use {'{rules}'}.
          </div>
        </div>

        <div className="field">
          <label htmlFor="violation-message">Message</label>
          <textarea
            id="violation-message"
            rows={3}
            value={draft.message}
            onChange={(event) => set({ message: event.target.value })}
            data-testid="violation-message"
          />
          <div className={measure.messageOver ? 'hint warn' : 'hint'} data-testid="violation-message-count">
            {measure.messageLength} of {limits.message} characters. {'{app}'}, {'{rules}'} and {'{severity}'} are filled in
            on the machine; put {'{rules}'} last, because the list of rules has no fixed length.
          </div>
          {measure.hiddenWhenCollapsed && (
            <div className="hint warn" data-testid="violation-message-hidden">
              A collapsed notification shows about the first {VISIBLE_WITHOUT_EXPANDING} characters. Move the practice
              earlier, or the person has to expand the notification to read it.
            </div>
          )}
        </div>

        <div className="notice-preview" data-testid="violation-preview" aria-label="What the person sees">
          <strong>{previewNotice(draft.title, draft.reference) || ' '}</strong>
          <span>{previewNotice(draft.message, draft.reference) || ' '}</span>
        </div>
        <p className="hint">
          A preview, with sample values for what the agent fills in. It shows no prompt text, and neither does the real one.
        </p>

        <div className="toolbar">
          <button
            type="submit"
            className="primary"
            disabled={busy || !draft.name.trim() || measure.titleOver || measure.messageOver}
            data-testid="save-violation-message"
          >
            Save
          </button>
          <button type="button" onClick={onCancel}>
            Cancel
          </button>
        </div>
      </form>
    </section>
  )
}
